// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, only use the separate dev API server when running on localhost.
  // This avoids mixed-content/CORS issues if NODE_ENV=development is used on a remote/staging host.
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

export const uploadFile = body => {
  return post('/api/upload-file', body);
};

export const deleteFile = body => {
  // Send as JSON since server expects req.body to be parsed JSON
  return request('/api/delete-file', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body != null;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : body != null ? { body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit) when a body is provided.
    headers: headers || (body != null ? { 'Content-Type': 'application/transit+json' } : {}),
    ...bodyMaybe,
    ...rest,
  };

  console.log('🔵 [API Request]', options.method || 'GET', url);

  return window
    .fetch(url, fetchOptions)
    .catch(fetchError => {
      console.error('❌ [Fetch Failed]', {
        url,
        method: options.method || 'GET',
        error: fetchError.message,
        stack: fetchError.stack,
      });
      throw fetchError;
    })
    .then(res => {
      const contentTypeHeader = res.headers.get('Content-Type');
      const contentType = contentTypeHeader
        ? contentTypeHeader
            .split(';')[0]
            .trim()
            .toLowerCase()
        : '';

      if (res.status >= 400) {
        // Read body once to avoid "body stream already read" errors.
        return res.text().then(text => {
          const snippet = (text || '').slice(0, 300); // avoid huge payloads

          // Try to parse as JSON first, regardless of Content-Type header
          try {
            const data = JSON.parse(text || '{}');
            if (data && (data.message || data.error || data.errors)) {
              const message = data.message || data.error || res.statusText || 'Request failed';
              const err = Object.assign(new Error(message), data, { status: res.status });
              throw err;
            }
          } catch (jsonErr) {
            if (jsonErr.status === res.status) {
              throw jsonErr;
            }
          }

          if (contentType === 'application/transit+json') {
            try {
              const data = deserialize(text);
              const message = data?.message || res.statusText || 'Request failed';
              const err = Object.assign(new Error(message), data, { status: res.status });
              throw err;
            } catch (_) {
              const err = new Error(snippet || res.statusText || 'Request failed');
              err.status = res.status;
              throw err;
            }
          }

          if (contentType === 'application/json') {
            try {
              const data = JSON.parse(text || '{}');
              const message = data?.message || data?.error || res.statusText || 'Request failed';
              const err = Object.assign(new Error(message), data, { status: res.status });
              throw err;
            } catch (_) {
              const err = new Error(snippet || res.statusText || 'Request failed');
              err.status = res.status;
              throw err;
            }
          }

          // Fallback: treat as text (e.g., HTML error page)
          const err = new Error(snippet || res.statusText || 'Request failed');
          err.status = res.status;
          throw err;
        });
      }

      if (contentType === 'application/transit+json') {
        return res.text().then(deserialize);
      } else if (contentType === 'application/json') {
        return res.json();
      }
      return res.text();
    });
};

// Generic GET helper function
const get = (path, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
  };

  return request(path, requestOptions);
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// JSON POST helper for custom endpoints
const postJson = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  };
  // Debug: log outgoing JSON POSTs to help trace issues
  try {
    // Avoid logging large blobs
    const preview = typeof body === 'object' ? JSON.parse(JSON.stringify(body)) : body;
    // eslint-disable-next-line no-console
    console.log('[api.postJson] ->', path, preview);
  } catch (_) {
    // ignore logging failures
  }
  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Get all users for admin to review
export const getAllUsers = body => {
  return post('/api/admin/users', body); // Options for Paginations
};

export const getUserListings = body => {
  return post('/api/user/listing', body);
};

// Get status wise users for admin to review
export const getStatusWiseUsers = body => {
  return post('/api/admin/users/status', body);
};

// Approve user
export const approveUser = body => {
  return post('/api/admin/users/approve', body);
};

// Reject user
export const rejectUser = body => {
  return post('/api/admin/users/reject', body);
};

// Suspend user
export const suspendUser = body => {
  return post('/api/admin/users/suspend', body);
};

// Admin: fetch tutor verification / decision audit logs
export const adminListTutorDecisionLogs = body => {
  return post('/api/admin/tutor/decision-logs', body);
};

// Update listing
export const updateListing = body => {
  return post('/api/admin/listing/update', body);
};

// Set or unset Super Admin for a user
export const setSuperAdmin = body => {
  return post('/api/admin/users/set-superadmin', body);
};

export const autoApproveBlogListings = body => {
  return post('/api/admin/listings/approve-blogs', body);
};

// ZOOM
export const zoomSchedule = body => {
  return post('/api/zoom/schedule', body);
};

export const zoomCallback = body => {
  return post('/api/zoom/callback', body);
};

export const zoomCreateMeetingWithRefreshToken = body => {
  return post('/api/zoom/create-meeting-with-refresh-token', body);
};

export const createZoomSignature = body => {
  return post('/api/zoom/create-signature', body);
};

export const createMeetingWithServerAccount = body => {
  return post('/api/zoom/create-meeting-with-server-account', body);
};

// Record a Zoom join initiated from the app (JSON)
export const zoomJoin = body => {
  return postJson('/api/zoom/join', body);
};
export const rescheduleBooking = body => {
  return postJson('/api/booking/reschedule', body);
};
export const requestRescheduleBooking = body => {
  return postJson('/api/booking/request-reschedule', body);
};
export const respondRescheduleRequestBooking = body => {
  return postJson('/api/booking/respond-reschedule-request', body);
};
export const fetchRescheduleCount = () => {
  return request('/api/booking/reschedule-count', { method: 'GET' });
};
export const cancelBooking = body => {
  return postJson('/api/booking/cancel', body);
};
// Ratings: recompute listing rating summary from reviews and persist to listing.publicData
export const recomputeListingRatings = body => {
  return post('/api/ratings/recompute', body);
};
// Wallet API
export const walletHold = body => {
  return post('/api/wallet/hold', body);
};

export const walletRelease = body => {
  return post('/api/wallet/release', body);
};

export const walletCapture = body => {
  return post('/api/wallet/capture', body);
};
export const walletDebit = body => {
  return post('/api/wallet/debit', body);
};
export const walletCredit = body => {
  return post('/api/wallet/credit', body);
};
export const walletBalance = body => {
  return post('/api/wallet/balance', body);
};
export const walletHistory = body => {
  return post('/api/wallet/history', body);
};

// Wallet summary
export const walletSummary = body => {
  return post('/api/wallet/summary', body);
};

// Subscription API
export const subscriptionStatus = body => {
  return post('/api/subscription/status', body);
};

export const subscriptionCancel = body => {
  return post('/api/subscription/cancel', body);
};

export const subscriptionPause = body => {
  return post('/api/subscription/pause', body);
};

export const subscriptionResume = body => {
  return post('/api/subscription/resume', body);
};

export const subscriptionChangeAmount = body => {
  return post('/api/subscription/change-amount', body);
};

export const adminExtendExpiry = body => {
  return post('/api/admin/wallet/extend-expiry', body);
};

// Reward API
export const rewardSummary = body => {
  return postJson('/api/reward/summary', body);
};

export const rewardHistory = body => {
  return post('/api/reward/history', body);
};

export const rewardDebit = body => {
  return post('/api/reward/debit', body);
};

export const activateStreakPause = body => {
  return post('/api/reward/streak-pause/activate', body);
};
// Record a login activity to update streak on login
export const rewardLoginActivity = body => {
  return post('/api/reward/login-activity', body);
};

// Trigger lesson-complete reward immediately when a session is marked complete
export const rewardLessonComplete = body => {
  return post('/api/reward/lesson-complete', body);
};

// Trigger review reward immediately when a customer submits a review
export const rewardReviewReward = body => {
  return post('/api/reward/review-reward', body);
};

// Withdrawal Request Flow
export const walletWithdrawRequest = body => {
  return post('/api/wallet/withdraw/request', body);
};

// Admin endpoints for withdrawals
export const adminListWithdrawals = body => {
  return post('/api/admin/wallet/withdrawals', body);
};
export const adminApproveWithdrawal = body => {
  return post('/api/admin/wallet/withdrawals/approve', body);
};
export const adminRejectWithdrawal = body => {
  return post('/api/admin/wallet/withdrawals/reject', body);
};
export const adminConfirmWithdrawal = body => {
  return post('/api/admin/wallet/withdrawals/confirm', body);
};

// Get refresh token
export const getRefreshToken = body => {
  return post('/api/get-refresh-token', body);
};

// Fetch provider & customer emails (Integration API via server)
export const getTransactionParties = body => {
  // This endpoint returns JSON
  return postJson('/api/get-transaction-parties', body);
};

// Create Google Calendar event via backend route
export const createGoogleEvent = body => {
  return postJson('/api/google/create-event', body);
};
export const deleteGoogleEvent = body => {
  return postJson('/api/google/delete-event', body);
};
// Create Google Calendar event via backend route
export const createReferralCode = body => {
  return post('/api/create-referral-code', body);
};

export const checkUserReferralCode = body => {
  return post('/api/check-referral-code', body);
};

export const validateReferralCode = body => {
  return post('/api/validate-referral-code', body);
};

export const updateReferralOnSignup = body => {
  return post('/api/update-referral-on-signup', body);
};

// Admin: list referrals
export const adminListReferrals = body => {
  return post('/api/admin/referrals', body);
};
// Admin: tutor warnings
export const adminListWarnings = body => {
  return post('/api/admin/warnings', body);
};
export const adminToggleWarningReview = body => {
  return post('/api/admin/warnings/toggle-review', body);
};
export const adminClearTutorWarnings = body => {
  return post('/api/admin/warnings/clear', body);
};
export const adminblogsList = body => {
  return post('/api/admin/blogs/listings', body);
};
// Admin: show single listing (includes drafts) via Integration SDK
export const adminShowListing = body => {
  return post('/api/admin/listings/show', body);
};
export const adminOpenListing = body => {
  return post('/api/admin/listings/open', body);
};
export const adminCloseListing = body => {
  return post('/api/admin/listings/close', body);
};

// tap payment gateway api

export const onFetchCustomerCards = body => {
  return post('/api/fetch-customer-cards', body);
};

export const onRetrieveCharge = body => {
  return post('/api/retrieve-charge', body);
};

export const onCreateTokenForSavedCard = body => {
  return postJson('/api/create-token-for-saved-cards', body);
};

export const onCreateCharge = body => {
  return post('/api/create-charge', body);
};

export const updatePaymentInDB = body => {
  return post('/api/updatePaymentInDB', body);
};

export const deleteCard = body => {
  return post('/api/deleteCard', body);
};

// Initialize a payment record before calling payment gateway
export const initPaymentInDB = body => {
  return postJson('/api/payment/init', body);
};

// Trigger Trial Auto Complete flow on the server (JSON)
// This nudges server to run runTrialAutoComplete safely with server credentials
export const trialAutoCompleteTrigger = async body => {
  // Swallow 429 cooldown responses to avoid console noise
  try {
    return await postJson('/api/cron/trial-autocomplete', body);
  } catch (err) {
    if (err && err.status === 429) {
      // Resolve gracefully to indicate cooldown; caller can ignore or adapt
      return { status: 'cooldown', retryAfterMs: err.retryAfterMs };
    }
    throw err;
  }
};

export const trialEligibility = body => {
  return postJson('/api/trial/eligibility', body);
};

export const sendContactUs = body => {
  return postJson('/api/contact-us', body);
};

export const fetchFxRates = () => {
  return request('/api/fx-rates', { method: 'GET' });
};

export const initStripeIntent = body => {
  return postJson('/api/payment/stripe/init-intent', body);
};

export const confirmStripePayment = body => {
  return postJson('/api/payment/stripe/confirm', body);
};

export const fetchStudentActiveBookings = () => {
  return request('/api/student-active-bookings', { method: 'GET' });
};

// Lesson Packs
export const purchaseLessonPack = body => {
  return postJson('/api/lesson-pack/purchase', body);
};

export const redeemLessonPack = body => {
  return postJson('/api/lesson-pack/redeem', body);
};

export const cancelLessonPack = body => {
  return postJson('/api/lesson-pack/cancel', body);
};

export const listLessonPacks = body => {
  return postJson('/api/lesson-pack/list', body);
};

export const lessonPackDetail = body => {
  return postJson('/api/lesson-pack/detail', body);
};

export const getActiveLessonPack = body => {
  return postJson('/api/lesson-pack/active', body);
};

export const fetchDisputeStatus = body => {
  return postJson('/api/dispute/status', body);
};

export const raiseDispute = body => {
  return postJson('/api/dispute/raise', body);
};

export const adminListDisputes = body => {
  return postJson('/api/admin/disputes', body);
};

export const adminResolveDispute = body => {
  return postJson('/api/admin/dispute/resolve', body);
};

export const refundTopup = body => {
  return postJson('/api/wallet/topup/refund', body);
};

// Reschedule and Cancel booking API helpers
// export const rescheduleBooking = body => {
//   return postJson('/api/reschedule-booking', body);
// };

// export const cancelBooking = body => {
//   return postJson('/api/cancel-booking', body);
// };

// ─── Family Plan API ───

export const familyStatus = body => {
  return post('/api/family/status', body);
};

export const familyChildRegister = body => {
  return post('/api/family/child/register', body);
};

export const familyChildCreate = body => {
  return post('/api/family/child/create', body);
};

export const familyChildApprove = body => {
  return post('/api/family/child/approve', body);
};

export const familyChildReject = body => {
  return post('/api/family/child/reject', body);
};

export const familyChildDelete = body => {
  return post('/api/family/child/delete', body);
};

export const adminListPendingDeletions = body => {
  return post('/api/admin/family/deletions', body);
};

export const adminConfirmDeleteChild = body => {
  return post('/api/admin/family/confirm-delete', body);
};

export const familyChildToggleBooking = body => {
  return post('/api/family/child/toggle-booking', body);
};

export const familyChildren = body => {
  return post('/api/family/children', body);
};

export const familyBookings = body => {
  return post('/api/family/bookings', body);
};

export const childInbox = body => {
  return post('/api/family/child/inbox', body);
};

export const childTransactionShow = body => {
  return post('/api/family/child/transaction-show', body);
};

export const childMessagesQuery = body => {
  return post('/api/family/child/messages-query', body);
};

export const childMessageSend = body => {
  return post('/api/family/child/message-send', body);
};

export const parentInbox = body => {
  return post('/api/family/parent/inbox', body);
};

export const parentTransactionShow = body => {
  return post('/api/family/parent/transaction-show', body);
};

export const parentMessagesQuery = body => {
  return post('/api/family/parent/messages-query', body);
};

export const sendParentMessage = body => {
  return post('/api/family/parent/message-send', body);
};

export const setChildHoliday = body => {
  return post('/api/family/child/holiday/set', body);
};

export const deleteChildHoliday = body => {
  return post('/api/family/child/holiday/delete', body);
};

export const listChildHolidays = body => {
  return post('/api/family/child/holiday/list', body);
};

// ─── Learning System API Helpers ──────────────────────────────────────────

export const getAvailableDialectCountries = (body = {}) => {
  return postJson('/api/learning/available-dialect-countries', body);
};

export const adminGetCountryOverrides = (body = {}) => {
  return postJson('/api/learning/admin/country-overrides/get', body);
};

export const adminSetCountryOverride = (body = {}) => {
  return postJson('/api/learning/admin/country-overrides/set', body);
};

export const submitLearningPlacement = (body = {}) => {
  return postJson('/api/learning/placement/submit', body);
};

export const getLearningPlacement = (body = {}) => {
  return postJson('/api/learning/placement/get', body);
};

export const getLearningProfile = (body = {}) => {
  return postJson('/api/learning/profile/get', body);
};

export const getAllLearningProfilesForParent = (body = {}) => {
  return postJson('/api/learning/profile/all-children', body);
};

export const submitLearningReport = (body = {}) => {
  return postJson('/api/learning/report/submit', body);
};

export const editLearningReport = (body = {}) => {
  return postJson('/api/learning/report/edit', body);
};

export const getLearningReportByTransaction = (body = {}) => {
  return postJson('/api/learning/report/get-by-tx', body);
};

export const getLearningProgressBars = (body = {}) => {
  return postJson('/api/learning/progress/bars', body);
};

export const checkLearningLevelUp = (body = {}) => {
  return postJson('/api/learning/progress/level-up-check', body);
};

export const confirmLearningLevelUp = (body = {}) => {
  return postJson('/api/learning/level-up/confirm', body);
};

export const getLearningLevelUpHistory = (body = {}) => {
  return postJson('/api/learning/level-up/history', body);
};

export const getLearningDashboard = (body = {}) => {
  return postJson('/api/learning/dashboard', body);
};

export const getLearningTimeline = (body = {}) => {
  return postJson('/api/learning/timeline', body);
};

export const getLearningTutorFormData = (body = {}) => {
  return postJson('/api/learning/tutor/form-data', body);
};

export const adminGetLearningOverview = (body = {}) => {
  return postJson('/api/admin/learning/overview', body);
};

export const adminListLearningProfiles = (body = {}) => {
  return postJson('/api/admin/learning/profiles', body);
};

export const adminSetLearningSkillLevel = (body = {}) => {
  return postJson('/api/admin/learning/set-level', body);
};

export const adminListLearningReports = body => {
  return post('/api/admin/learning/reports', body);
};

export const adminRetryReportNotification = body => {
  return post('/api/admin/learning/reports/retry-notification', body);
};

export const adminListUnsentReports = body => {
  return post('/api/admin/learning/reports/unsent', body);
};

export const adminGetLearningObjectivesStats = body => {
  return post('/api/admin/learning/stats/objectives', body);
};

export const adminGetLearningLevelUpStats = body => {
  return post('/api/admin/learning/stats/level-ups', body);
};

export const getCertificateDownloadUrl = ({ certificateId, childId, skill, level, locale } = {}) => {
  const params = [];
  if (certificateId) params.push(`certificateId=${encodeURIComponent(certificateId)}`);
  if (childId) params.push(`childId=${encodeURIComponent(childId)}`);
  if (skill) params.push(`skill=${encodeURIComponent(skill)}`);
  if (level) params.push(`level=${encodeURIComponent(level)}`);
  if (locale) params.push(`locale=${encodeURIComponent(locale)}`);
  const query = params.length > 0 ? `?${params.join('&')}` : '';
  const base = certificateId ? `/api/learning/certificate/download/${encodeURIComponent(certificateId)}` : '/api/learning/certificate/download';
  return `${apiBaseUrl()}${base}${query}`;
};

export const getTermlySummaryDownloadUrl = ({ childId, parentId, userId, locale } = {}) => {
  const params = [];
  if (childId) params.push(`childId=${encodeURIComponent(childId)}`);
  if (parentId) params.push(`parentId=${encodeURIComponent(parentId)}`);
  if (userId) params.push(`userId=${encodeURIComponent(userId)}`);
  if (locale) params.push(`locale=${encodeURIComponent(locale)}`);
  const query = params.length > 0 ? `?${params.join('&')}` : '';
  return `${apiBaseUrl()}/api/learning/progress/summary-pdf${query}`;
};

/**
 * Triggers dispatch of a phone verification code via Twilio Verify.
 * 
 * @param {Object} params
 * @param {string} params.phoneNumber - Phone number in E.164 format (e.g. +966501234567)
 * @returns {Promise<Object>} API response with delivery status
 */
export const sendPhoneVerificationCode = ({ phoneNumber }) => {
  return postJson('/api/phone/send-code', { phoneNumber });
};

/**
 * Validates a user-entered verification code with Twilio Verify.
 * 
 * @param {Object} params
 * @param {string} params.phoneNumber - Phone number in E.164 format
 * @param {string} params.code - 6-digit OTP code entered by the user
 * @returns {Promise<Object>} Verification status response
 */
export const checkPhoneVerificationCode = ({ phoneNumber, code }) => {
  return postJson('/api/phone/check-code', { phoneNumber, code });
};

/**
 * Fetches dynamic landing page section content overrides from the database.
 * 
 * @returns {Promise<Object>} Dictionary of section content keyed by sectionKey
 */
export const fetchLandingPageContent = () => {
  return get('/api/landing-page-content');
};

/**
 * Updates a landing page section's title, description, image, and audio in the database.
 * Restricted to administrators.
 * 
 * @param {Object} payload Section update parameters
 * @returns {Promise<Object>} Updated section document
 */
export const adminUpdateLandingPageSection = payload => {
  return postJson('/api/admin/landing-page-content/update', payload);
};

/**
 * Resets a landing page section (or 'all') back to default siteData.js values.
 * Restricted to administrators.
 * 
 * @param {Object} payload { currentUserId, sectionKey }
 * @returns {Promise<Object>} Success status
 */
export const adminResetLandingPageSection = payload => {
  return postJson('/api/admin/landing-page-content/reset', payload);
};




